import { parseUnits } from 'viem'

import { LotRow } from '~/app/accounting/lot-rows'

export type LotAssertionWrite = {
  kind: 'seed' | 'edit' | 'revoke' | 'designate'
  smartAccountId: string
  chainId: number
  tokenAddress: string
  anchor?: { sourceTransferId: string; originLotId: string | null }
  /** Names an assertion row directly, for keys with no lot to anchor to. */
  targetKey?: string
  /** The inbound a designation marks as drawing from seeded inventory. */
  transferId?: string
  /** USD per whole token, plain decimal string. */
  unitPrice?: string
  /** ISO 8601 timestamp. */
  acquisitionTime?: string
  /** Raw base units as an integer string. */
  quantity?: string
}

const partitionOf = (lot: LotRow) => ({
  smartAccountId: lot.smartAccountId,
  chainId: lot.chainId,
  tokenAddress: lot.tokenAddress,
})

// Plain decimal string; String() emits scientific notation below 1e-6, which
// NumberInput can't display and the assertion endpoint rejects.
export const toPlainAmount = (value: number) =>
  value.toFixed(18).replace(/\.?0+$/, '')

/**
 * Sparse by design: only fields that differ from the lot's current values are
 * asserted, so an untouched field stays derived and keeps flowing with the
 * facts on future rebuilds. Fields with a live assertion are always
 * resubmitted — the latest assertion row replaces the prior one wholesale, so
 * omitting them would silently revert the earlier edit.
 */
export const buildEditAssertionWrite = ({
  lot,
  unitPrice,
  acquisitionTime,
}: {
  lot: LotRow
  unitPrice: string
  acquisitionTime: Date | null
}): LotAssertionWrite | undefined => {
  if (lot.sourceTransferId === null) return undefined

  // NumberInput form values are canonical: dot decimal, no display separators.
  const price = unitPrice.trim()
  const priceChanged = price !== '' && Number(price) !== lot.unitPrice
  const timeChanged =
    acquisitionTime !== null &&
    acquisitionTime.getTime() !== new Date(lot.acquisitionTime).getTime()
  if (!priceChanged && !timeChanged) return undefined

  const includePrice = priceChanged || lot.assertedFields.unitPrice
  const includeTime = timeChanged || lot.assertedFields.acquisitionTime

  return {
    kind: 'edit',
    ...partitionOf(lot),
    anchor: {
      sourceTransferId: lot.sourceTransferId,
      originLotId: lot.originLotId,
    },
    ...(includePrice
      ? { unitPrice: price !== '' ? price : toPlainAmount(lot.unitPrice) }
      : {}),
    ...(includeTime
      ? {
          acquisitionTime: (
            acquisitionTime ?? new Date(lot.acquisitionTime)
          ).toISOString(),
        }
      : {}),
  }
}

/**
 * A seed plus a designation of the lot's source transfer, written together:
 * the seed is the pre-custody inventory (what was held, at what basis, since
 * when) and the designation routes it into the inbound that opened this lot,
 * so the user picks the migration transfer by picking its lot. Undefined
 * until every field is present and the acquisition date is in the past.
 */
export const buildSeedAssertionWrites = ({
  lot,
  quantity,
  unitPrice,
  acquisitionTime,
}: {
  lot: LotRow
  quantity: string
  unitPrice: string
  acquisitionTime: Date | null
}): LotAssertionWrite[] | undefined => {
  if (lot.sourceTransferId === null) return undefined
  const price = unitPrice.trim()
  const amount = quantity.trim()
  if (price === '' || amount === '' || Number(amount) <= 0) return undefined
  if (acquisitionTime === null || acquisitionTime.getTime() >= Date.now())
    return undefined

  return [
    {
      kind: 'seed',
      ...partitionOf(lot),
      quantity: parseUnits(amount, lot.decimals).toString(),
      unitPrice: price,
      acquisitionTime: acquisitionTime.toISOString(),
    },
    {
      kind: 'designate',
      ...partitionOf(lot),
      transferId: lot.sourceTransferId,
    },
  ]
}

/**
 * Withdraws an assertion by its own key rather than through a lot. A
 * superseded correction has no lot at its anchor by definition, so the anchor
 * form cannot name it.
 */
export const buildDismissAssertionWrite = (row: {
  smartAccountId: string
  chainId: number
  tokenAddress: string
  targetKey: string
}): LotAssertionWrite => ({
  kind: 'revoke',
  smartAccountId: row.smartAccountId,
  chainId: row.chainId,
  tokenAddress: row.tokenAddress,
  targetKey: row.targetKey,
})

/** Withdraws every edit asserted against the lot's anchor. */
export const buildRevokeAssertionWrite = (
  lot: LotRow,
): LotAssertionWrite | undefined => {
  if (lot.sourceTransferId === null) return undefined
  return {
    kind: 'revoke',
    ...partitionOf(lot),
    anchor: {
      sourceTransferId: lot.sourceTransferId,
      originLotId: lot.originLotId,
    },
  }
}
